import React from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle, CheckCircle2, Circle, Clock, MinusCircle, ExternalLink } from 'lucide-react';
import { LibraryEmptyState, LibraryErrorState, LibraryLoading } from '@/components/forms/LibraryStates';
import { STATUS_TONE, columnLabel, formatCell } from '@/lib/auditRegisters';

const TONE_ICON = { open: Circle, progress: Clock, done: CheckCircle2, muted: MinusCircle, alert: AlertTriangle, warn: AlertTriangle };
const TONE_CLASS = { open: 'text-sky-700', progress: 'text-amber-700', done: 'text-emerald-700', muted: 'text-muted-foreground', alert: 'text-red-700', warn: 'text-amber-700' };

// Status conveyed by text + glyph (never colour alone).
export function StatusBadge({ value, overdue }) {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  const key = String(value).toUpperCase(); const tone = STATUS_TONE[key] || 'open'; const Icon = TONE_ICON[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${TONE_CLASS[tone]}`} data-testid="register-status" data-status={key}>
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />{String(value).replace(/_/g, ' ')}
      {overdue && <span className="ml-1 rounded border border-red-300 px-1 text-xs text-red-700" data-testid="register-overdue-flag">Overdue</span>}
    </span>
  );
}

function SortHeader({ col, sort, onSort, sortable }) {
  const active = sort?.field === col; const dir = active ? sort.dir : null;
  if (!sortable) return <TableHead scope="col">{columnLabel(col)}</TableHead>;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead scope="col" aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(col)} className="inline-flex items-center gap-1 font-medium hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded"
        data-testid={`register-sort-${col}`} aria-label={`Sort by ${columnLabel(col)}${active ? (dir === 'asc' ? ', ascending' : ', descending') : ''}`}>
        {columnLabel(col)} <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </TableHead>
  );
}

export function RegisterTable({ register, columns, rows, total, page, pageSize, sort, onSort, onPage, loading, error, onRetry, onOpen, emptyMessage, renderActions }) {
  if (loading) return <LibraryLoading label={`Loading ${register?.label || 'register'}…`} />;
  if (error) return <LibraryErrorState error={error} onRetry={onRetry} />;
  if (!rows.length) return <LibraryEmptyState title={emptyMessage || 'No entries yet'} hint={register?.read_only ? 'Derived registers show authoritative records in your scope.' : 'Use “Add entry” to record the first entry.'} />;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const statusKey = columns.includes('status') ? 'status' : columns.includes('overall_status') ? 'overall_status' : columns.includes('authorisation_status') ? 'authorisation_status' : null;
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <Table data-testid="register-table">
          <caption className="sr-only">{register?.label}: {total} entries, page {page} of {pages}</caption>
          <TableHeader><TableRow>
            {columns.map((c) => <SortHeader key={c} col={c} sort={sort} onSort={onSort} sortable={(register?.sort_fields || []).includes(c)} />)}
            <TableHead scope="col"><span className="sr-only">Actions</span></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`register-row-${r.id}`} className="focus-within:bg-muted/40">
                {columns.map((c, i) => (
                  <TableCell key={c} className={i === 0 ? 'font-medium' : ''} data-testid={`cell-${c}`}>
                    {c === statusKey ? <StatusBadge value={r[c]} overdue={r.overdue} /> : c === 'requirements' ? <RequirementCells cells={r.cells} /> : formatCell(r[c])}
                  </TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  {onOpen && <Button variant="ghost" size="sm" onClick={() => onOpen(r)} data-testid={`register-open-${r.id}`}>View</Button>}
                  {register?.read_only && r.source_link && (
                    <a href={r.source_link} className="inline-flex items-center gap-1 text-sm underline ml-2 focus-visible:outline focus-visible:outline-2 rounded" data-testid={`register-source-link-${r.id}`}>
                      Source record <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /></a>
                  )}
                  {renderActions?.(r)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <nav className="flex items-center justify-between text-sm" aria-label="Pagination" data-testid="register-pagination">
        <span aria-live="polite">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="register-prev-page">Previous</Button>
          <span className="px-2 py-1">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="register-next-page">Next</Button>
        </div>
      </nav>
    </div>
  );
}

function RequirementCells({ cells }) {
  if (!cells) return '—';
  return (
    <ul className="flex flex-wrap gap-1 list-none p-0 m-0" aria-label="Requirement statuses">
      {Object.entries(cells).map(([k, v]) => <li key={k} className="text-xs rounded border px-1.5 py-0.5"><span className="font-medium">{k}</span>: <StatusBadge value={v.status} /></li>)}
    </ul>
  );
}
